import { useSession } from '@/store/session';
import { readOnlyFrom, useReadOnly } from '@/store/readonly';
import { logError, logInfo } from '@/lib/log';

/** API base: VITE_API_URL, else same-origin /v1 (Caddy proxies /v1 and /health to the API in production). */
export const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) || '/v1').replace(/\/+$/, '');

export interface FieldError {
  path: string;
  message: string;
}

/** An HTTP error from the API, with Nest's message and our zod field errors when present. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = null,
    readonly fieldErrors: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isNetwork() {
    return this.status === 0;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  /** Send the bearer token (default true). Public routes pass false. */
  auth?: boolean;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${url}?${qs}` : url;
}

/** Turn a Nest error body ({ message, errors? } or { message: string[] }) into readable text. */
export function errorMessage(body: unknown, fallback: string): { message: string; fields: FieldError[] } {
  if (!body || typeof body !== 'object') return { message: fallback, fields: [] };
  const b = body as { message?: unknown; errors?: unknown };
  const fields = Array.isArray(b.errors) ? (b.errors as FieldError[]).filter((e) => e && typeof e.message === 'string') : [];
  let message = fallback;
  if (typeof b.message === 'string') message = b.message;
  else if (Array.isArray(b.message)) message = b.message.join('; ');
  else if (b.message && typeof b.message === 'object' && typeof (b.message as { message?: unknown }).message === 'string') message = (b.message as { message: string }).message;
  if (fields.length && message === 'Validation failed') message = fields.map((f) => (f.path ? `${f.path}: ${f.message}` : f.message)).join('; ');
  return { message, fields };
}

/** Headers for the current session: bearer token and X-Workspace-Id (only needed with several workspaces). */
export function sessionHeaders(): Record<string, string> {
  const s = useSession.getState();
  const h: Record<string, string> = {};
  if (s.token) h.Authorization = `Bearer ${s.token}`;
  if (s.workspaceId && s.memberships.length > 1) h['X-Workspace-Id'] = s.workspaceId;
  return h;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = { Accept: 'application/json', ...(opts.auth === false ? {} : sessionHeaders()) };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: opts.body === undefined ? undefined : JSON.stringify(opts.body), signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    logError('api', `network error ${method} ${path}`, e);
    throw new ApiError(0, 'network', null);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const { message, fields } = errorMessage(body, res.statusText || `HTTP ${res.status}`);
    logError('api', `${method} ${path} → ${res.status} ${message}`);
    if (res.status === 401 && opts.auth !== false && useSession.getState().token) {
      logInfo('auth', 'session expired or invalid; signing out');
      useSession.getState().logout();
    }
    const readOnly = res.status === 403 ? readOnlyFrom(body) : null;
    if (readOnly) {
      logInfo('api', `workspace is read-only (${readOnly.code}, billing=${readOnly.billing})`);
      useReadOnly.getState().set({ workspaceId: useSession.getState().workspaceId, ...readOnly });
    }
    throw new ApiError(res.status, message, body, fields);
  }
  return body as T;
}

export const http = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'method' | 'query'>) => request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T = void>(path: string, opts?: Omit<RequestOptions, 'method'>) => request<T>(path, { ...opts, method: 'DELETE' }),
};
